/**
 * Pure utility functions for parsing term IDs.
 * Extracted from termUtils.ts to prevent circular dependencies.
 *
 *  * These functions have no dependencies on selectors and only perform string parsing.
 */

import type { OfferType } from '@makemydeal/dr-platform-types';
import type { TermIdParam, TermIdParams } from '../types/paymentTypes';
import { KEYID_DELIMETER, MIN_ID_PARAMS } from '../types/commonTypes';

/**
 * Joins term values with proper decimal formatting
 */
export const joinTermValues = (values: string[] | number[]): number => {
    const joinedNumber =
        values.length < 2
            ? values.join('')
            : [values.slice(0, values.length - 1).join(''), values.slice(values.length - 1)].join('.');
    return Number(joinedNumber);
};

/**
 * Validates if an extension value is valid (not null, undefined, 0, empty string, or "0")
 */
export const isExtensionValueValid = (value: string | number): boolean => {
    return ![null, undefined, 0, '', '0'].includes(value);
};

/**
 * Parses a term ID string into its component parts
 * @param termId - The term ID string to parse
 * @returns Parsed term ID parameters including offer type and term ID parameters
 */
export const parseTermId = (termId?: string): TermIdParams => {
    if (!termId) return {} as TermIdParams;

    const idParts = termId.split(KEYID_DELIMETER);
    if (idParts.length < MIN_ID_PARAMS) {
        return {} as TermIdParams;
    }

    const offerType = idParts.shift() as OfferType;
    const termIdParams: TermIdParam = {};

    let currentKey: string | undefined;
    const currentValues: string[] = [];

    for (const part of idParts) {
        const isNumeric = /^[0-9]+$/.test(part);

        if (!currentKey) {
            // First part must be a key
            currentKey = part;
        } else if (isNumeric) {
            // Numeric parts are collected as values
            currentValues.push(part);
        } else {
            // Non-numeric part starts a new key
            if (currentValues.length > 0) {
                termIdParams[currentKey] = joinTermValues(currentValues);
            }
            currentKey = part;
            currentValues.length = 0; // Clear array
        }
    }

    // Handle the last key-value pair
    if (currentKey && currentValues.length > 0) {
        termIdParams[currentKey] = joinTermValues(currentValues);
    }

    return { offerType, termIdParams };
};
